import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getDashboardData } from '../../actions/dashboard';
import { 
    Users, 
    ShoppingCart, 
    Package, 
    TrendingUp,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    BarChart3,
    UserPlus,
    Percent
} from 'lucide-react';
import { Spinner, Alert } from 'react-bootstrap';

const Dashboard = ({ 
    getDashboardData,
    dashboardStats,
    dashboardRevenue,
    dashboardLoading
}) => {
    const [selectedPeriod, setSelectedPeriod] = useState('last24Hours');

    useEffect(() => {
        document.title = 'Dashboard';
        getDashboardData();
    }, [getDashboardData]);

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold">
                                    <BarChart3 size={32} className="me-3 text-primary" />
                                    Dashboard Overview
                                </h1>
                                <p className="text-muted mb-0">
                                    Real-time business statistics and analytics
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Loading State */}
                {dashboardLoading && (
                    <div className="row">
                        <div className="col-12">
                            <div className="card shadow-sm">
                                <div className="card-body text-center py-5">
                                    <Spinner animation="border" variant="primary" size="lg" />
                                    <p className="mt-3 text-muted">Loading dashboard data...</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Dashboard Statistics Section */}
                {!dashboardLoading && dashboardStats && (
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-white border-bottom">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <BarChart3 size={20} className="me-2 text-primary" />
                                        Dashboard Statistics
                                    </h5>
                                </div>
                                <div className="card-body">
                                    {/* Time Period Tabs */}
                                    <div className="btn-group mb-4" role="group">
                                        <button 
                                            type="button" 
                                            className={`btn ${selectedPeriod === 'last24Hours' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setSelectedPeriod('last24Hours')}
                                        >
                                            <Clock size={16} className="me-1" />
                                            Last 24 Hours
                                        </button>
                                        <button 
                                            type="button" 
                                            className={`btn ${selectedPeriod === 'lastWeek' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setSelectedPeriod('lastWeek')}
                                        >
                                            Last Week
                                        </button>
                                        <button 
                                            type="button" 
                                            className={`btn ${selectedPeriod === 'lastMonth' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setSelectedPeriod('lastMonth')}
                                        >
                                            Last Month
                                        </button>
                                    </div>

                                    {/* Users Statistics */}
                                    <div className="row mb-4">
                                        <div className="col-12">
                                            <h6 className="text-primary mb-3">
                                                <Users className="me-2" size={20} />
                                                Users
                                            </h6>
                                            <div className="row g-3">
                                                <div className="col-md-2 col-sm-6">
                                                    <div className="card bg-light border-0 h-100">
                                                        <div className="card-body text-center">
                                                            <small className="text-muted d-block mb-2">Last 24h</small>
                                                            <h4 className="mb-0">{dashboardStats.users?.last24Hours || 0}</h4>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-2 col-sm-6">
                                                    <div className="card bg-light border-0 h-100">
                                                        <div className="card-body text-center">
                                                            <small className="text-muted d-block mb-2">Last Week</small>
                                                            <h4 className="mb-0">{dashboardStats.users?.lastWeek || 0}</h4>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-2 col-sm-6">
                                                    <div className="card bg-light border-0 h-100">
                                                        <div className="card-body text-center">
                                                            <small className="text-muted d-block mb-2">Last Month</small>
                                                            <h4 className="mb-0">{dashboardStats.users?.lastMonth || 0}</h4>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-2 col-sm-6">
                                                    <div className="card bg-light border-0 h-100">
                                                        <div className="card-body text-center">
                                                            <small className="text-muted d-block mb-2">Last Quarter</small>
                                                            <h4 className="mb-0">{dashboardStats.users?.lastQuarter || 0}</h4>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-2 col-sm-6">
                                                    <div className="card bg-primary text-white border-0 h-100">
                                                        <div className="card-body text-center">
                                                            <small className="d-block mb-2">Total</small>
                                                            <h4 className="mb-0">{dashboardStats.users?.total || 0}</h4>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Customer Conversion Section */}
                                    {(dashboardStats.newUsers || dashboardStats.customers || dashboardStats.conversionRate) && (
                                        <div className="row mb-4">
                                            <div className="col-12">
                                                <h6 className="text-primary mb-3">
                                                    <UserPlus className="me-2" size={20} />
                                                    Customer Conversion - {selectedPeriod === 'last24Hours' ? 'Last 24 Hours' : selectedPeriod === 'lastWeek' ? 'Last Week' : 'Last Month'}
                                                </h6>
                                                <div className="row g-3">
                                                    {/* New Users */}
                                                    <div className="col-md-4 col-sm-6">
                                                        <div className="card bg-info text-white border-0 h-100">
                                                            <div className="card-body text-center">
                                                                <UserPlus className="mb-2" size={32} />
                                                                <div className="h5 mb-1">{dashboardStats.newUsers?.[selectedPeriod] || 0}</div>
                                                                <small>New Users</small>
                                                                <p className="small mt-2 mb-0 text-white-50">
                                                                    Not yet purchased
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* Customers */}
                                                    <div className="col-md-4 col-sm-6">
                                                        <div className="card bg-success text-white border-0 h-100">
                                                            <div className="card-body text-center">
                                                                <CheckCircle className="mb-2" size={32} />
                                                                <div className="h5 mb-1">{dashboardStats.customers?.[selectedPeriod] || 0}</div>
                                                                <small>New Customers</small>
                                                                <p className="small mt-2 mb-0 text-white-50">
                                                                    Made their first purchase
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* Conversion Rate */}
                                                    <div className="col-md-4 col-sm-6">
                                                        <div className="card bg-primary text-white border-0 h-100">
                                                            <div className="card-body text-center">
                                                                <Percent className="mb-2" size={32} />
                                                                <div className="h5 mb-1">{dashboardStats.conversionRate?.[selectedPeriod] || '0'}%</div>
                                                                <small>Conversion Rate</small>
                                                                <p className="small mt-2 mb-0 text-white-50">
                                                                    User to Customer
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* Total New Users */}
                                                    <div className="col-md-4 col-sm-6">
                                                        <div className="card bg-warning text-white border-0 h-100">
                                                            <div className="card-body text-center">
                                                                <UserPlus className="mb-2" size={32} />
                                                                <div className="h5 mb-1">{dashboardStats.newUsers?.total || 0}</div>
                                                                <small>Total New Users</small>
                                                                <p className="small mt-2 mb-0 text-white-50">
                                                                    Waiting to purchase
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* Total Customers */}
                                                    <div className="col-md-4 col-sm-6">
                                                        <div className="card bg-success border-3 border-warning text-white h-100">
                                                            <div className="card-body text-center">
                                                                <CheckCircle className="mb-2" size={32} />
                                                                <div className="h5 mb-1">{dashboardStats.customers?.total || 0}</div>
                                                                <small>Total Customers</small>
                                                                <p className="small mt-2 mb-0 text-white-50">
                                                                    Made at least one purchase
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Orders Statistics */}
                                    {dashboardStats.orders && (
                                        <div className="row mb-4">
                                            <div className="col-12">
                                                <h6 className="text-primary mb-3">
                                                    <ShoppingCart className="me-2" size={20} />
                                                    Orders - {selectedPeriod === 'last24Hours' ? 'Last 24 Hours' : selectedPeriod === 'lastWeek' ? 'Last Week' : 'Last Month'}
                                                </h6>
                                                <div className="row g-3">
                                                    <div className="col-md-3 col-sm-6">
                                                        <div className="card bg-info text-white border-0 h-100">
                                                            <div className="card-body text-center">
                                                                <ShoppingCart className="mb-2" size={32} />
                                                                <div className="h5 mb-1">{dashboardStats.orders[selectedPeriod]?.total || 0}</div>
                                                                <small>Total Orders</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3 col-sm-6">
                                                        <div className="card bg-success text-white border-0 h-100">
                                                            <div className="card-body text-center">
                                                                <CheckCircle className="mb-2" size={32} />
                                                                <div className="h5 mb-1">{dashboardStats.orders[selectedPeriod]?.success || 0}</div>
                                                                <small>Successful</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3 col-sm-6">
                                                        <div className="card bg-danger text-white border-0 h-100">
                                                            <div className="card-body text-center">
                                                                <XCircle className="mb-2" size={32} />
                                                                <div className="h5 mb-1">{dashboardStats.orders[selectedPeriod]?.failed || 0}</div>
                                                                <small>Failed</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3 col-sm-6">
                                                        <div className="card bg-warning text-white border-0 h-100">
                                                            <div className="card-body text-center">
                                                                <AlertCircle className="mb-2" size={32} />
                                                                <div className="h5 mb-1">{dashboardStats.orders[selectedPeriod]?.cancelled || 0}</div>
                                                                <small>Cancelled</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Delivery Can Transactions Statistics */}
                                    {dashboardStats.deliveryCanTransactions && (
                                        <div className="row mb-4">
                                            <div className="col-12">
                                                <h6 className="text-primary mb-3">
                                                    <Package className="me-2" size={20} />
                                                    Delivery Transactions - {selectedPeriod === 'last24Hours' ? 'Last 24 Hours' : selectedPeriod === 'lastWeek' ? 'Last Week' : 'Last Month'}
                                                </h6>
                                                <div className="row g-3">
                                                    <div className="col-md-2 col-sm-6">
                                                        <div className="card bg-info text-white border-0 h-100">
                                                            <div className="card-body text-center">
                                                                <div className="h5 mb-1">{dashboardStats.deliveryCanTransactions[selectedPeriod]?.total || 0}</div>
                                                                <small>Total</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-2 col-sm-6">
                                                        <div className="card bg-success text-white border-0 h-100">
                                                            <div className="card-body text-center">
                                                                <div className="h5 mb-1">{dashboardStats.deliveryCanTransactions[selectedPeriod]?.success || 0}</div>
                                                                <small>Delivered</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-2 col-sm-6">
                                                        <div className="card bg-danger text-white border-0 h-100">
                                                            <div className="card-body text-center">
                                                                <div className="h5 mb-1">{dashboardStats.deliveryCanTransactions[selectedPeriod]?.failed || 0}</div>
                                                                <small>Failed</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-2 col-sm-6">
                                                        <div className="card bg-warning text-white border-0 h-100">
                                                            <div className="card-body text-center">
                                                                <div className="h5 mb-1">{dashboardStats.deliveryCanTransactions[selectedPeriod]?.cancelled || 0}</div>
                                                                <small>Cancelled</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-2 col-sm-6">
                                                        <div className="card bg-primary text-white border-0 h-100">
                                                            <div className="card-body text-center">
                                                                <div className="h5 mb-1">{dashboardStats.deliveryCanTransactions[selectedPeriod]?.inProgress || 0}</div>
                                                                <small>In Progress</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-2 col-sm-6">
                                                        <div className="card bg-secondary text-white border-0 h-100">
                                                            <div className="card-body text-center">
                                                                <div className="h5 mb-1">{dashboardStats.deliveryCanTransactions[selectedPeriod]?.pending || 0}</div>
                                                                <small>Pending</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Revenue Statistics */}
                                    {dashboardRevenue && (
                                        <div className="row">
                                            <div className="col-12">
                                                <h6 className="text-primary mb-3">
                                                    <TrendingUp className="me-2" size={20} />
                                                    Revenue (₹)
                                                </h6>
                                                <div className="row g-3">
                                                    <div className="col-md-3 col-sm-6">
                                                        <div className="card bg-light border-0 h-100">
                                                            <div className="card-body text-center">
                                                                <small className="text-muted d-block mb-2">Last 24h</small>
                                                                <h4 className="mb-0">₹{dashboardRevenue.revenue?.last24Hours?.toLocaleString() || 0}</h4>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3 col-sm-6">
                                                        <div className="card bg-light border-0 h-100">
                                                            <div className="card-body text-center">
                                                                <small className="text-muted d-block mb-2">Last Week</small>
                                                                <h4 className="mb-0">₹{dashboardRevenue.revenue?.lastWeek?.toLocaleString() || 0}</h4>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3 col-sm-6">
                                                        <div className="card bg-light border-0 h-100">
                                                            <div className="card-body text-center">
                                                                <small className="text-muted d-block mb-2">Last Month</small>
                                                                <h4 className="mb-0">₹{dashboardRevenue.revenue?.lastMonth?.toLocaleString() || 0}</h4>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3 col-sm-6">
                                                        <div className="card bg-success text-white border-0 h-100">
                                                            <div className="card-body text-center">
                                                                <small className="d-block mb-2">Total Revenue</small>
                                                                <h4 className="mb-0">₹{dashboardRevenue.revenue?.total?.toLocaleString() || 0}</h4>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Empty State - No Data Available */}
                {!dashboardLoading && !dashboardStats && (
                    <div className="row">
                        <div className="col-12">
                            <Alert variant="info" className="text-center">
                                <BarChart3 size={48} className="mb-3 text-primary" />
                                <h4>No Dashboard Data Available</h4>
                                <p className="text-muted mb-0">
                                    Dashboard statistics will appear here once data is available.
                                </p>
                            </Alert>
                        </div>
                    </div>
                )}

            </div>
        </Fragment>
    );
};

Dashboard.propTypes = {
    getDashboardData: PropTypes.func.isRequired,
    dashboardStats: PropTypes.object,
    dashboardRevenue: PropTypes.object,
    dashboardLoading: PropTypes.bool
};

const mapStateToProps = state => ({
    dashboardStats: state.dashboard.stats,
    dashboardRevenue: state.dashboard.revenue,
    dashboardLoading: state.dashboard.loading
});

export default connect(mapStateToProps, { getDashboardData })(Dashboard);

